/* eslint-disable prettier/prettier */
import { FunctionComponent, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { handlingUrlParams } from "../../Utils/AppUtils";
import AdminServices from "services/admin-services";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { SplitButton } from "primereact/splitbutton";
import { MenuItem } from "primereact/menuitem";
import { confirmDialog } from "primereact/confirmdialog";
import moment from "moment";
import AddPackage from "./Addpackage";
import { setToaster } from "common/toastStore";

interface PackagesProps {}

const Packages: FunctionComponent<PackagesProps> = () => {
  const [showPackageModel, setShowPackageModel] = useState<boolean>(false);
  const [customerData, setCustomerData] = useState([] as any);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
  });

  useEffect(() => {
    loadLazyData();
  }, [lazyParams]);

  const loadLazyData = () => {
    setLoading(true);
    AdminServices.getPackages(
      handlingUrlParams({
        PageNumber: lazyParams.page,
        PageSize: lazyParams.rows,
      }),
    )
      .then((response: any) => {
        const getHeaders = response.headers;
        const parsedHeaders = JSON.parse(getHeaders["x-pagination"]);
        setCustomerData(response.data);
        setTotalRecords(parsedHeaders.TotalCount);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
      });
  };

  const onPage = (event: any) => {
    setLazyParams({
      ...lazyParams,
      first: event.first, // index of first record in current page
      rows: event.rows, // rows per page
      page: event.page + 1, // 1-based page for backend
    });
  };

  const onSort = (event: any) => {
    setLazyParams(event);
  };

  const formatDateTime = (dateValue?: string | null) => {
    if (!dateValue || !moment(dateValue).isValid()) return "NA";
    return moment(dateValue).format("MM/DD/YYYY hh:mm A");
  };

  const renderCreatedDate = (rowData: any) => {
    return <span>{formatDateTime(rowData?.createdDate)}</span>;
  };

  const renderUpdatedDate = (rowData: any) => {
    return <span>{formatDateTime(rowData?.updatedDate)}</span>;
  };

  const renderActive = (rowData: any) => {
    const isActive = !!rowData?.isActive;
    return (
      <span
        className={
          isActive ? "text-green-700 font-medium" : "text-red-700 font-medium"
        }
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const handleViewPackage = (rowData: any) => {
    setToaster({
      severity: "info",
      summary: "View Package",
      detail: `View action selected for "${rowData?.name || "Package"}".`,
    });
  };

  const handleEditPackage = (rowData: any) => {
    setToaster({
      severity: "info",
      summary: "Edit Package",
      detail: `Edit action selected for "${rowData?.name || "Package"}".`,
    });
  };

  const handleDeletePackage = (rowData: any) => {
    confirmDialog({
      message: `Are you sure you want to delete package "${
        rowData?.name || ""
      }"?`,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        setToaster({
          severity: "info",
          summary: "Delete Package",
          detail: `Delete action selected for "${rowData?.name || "Package"}".`,
        });
      },
    });
  };

  const renderActions = (rowData: any) => {
    const actionItems: MenuItem[] = [
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => handleEditPackage(rowData),
      },
      {
        label: "View",
        icon: "pi pi-eye",
        command: () => handleViewPackage(rowData),
      },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () => handleDeletePackage(rowData),
      },
    ];

    return (
      <SplitButton
        label="Actions"
        icon="pi pi-cog"
        model={actionItems}
        className="p-button-sm p-button-outlined p-button-secondary"
        onClick={() => handleViewPackage(rowData)}
      />
    );
  };

  const renderFilters = () => {
    return (
      <div className="flex flex-row justify-between items-center">
        <h2 className="text-xl font-bold">List of Packages</h2>
        <Button
          onClick={showOverpanel}
          label="Add Package"
          className="p-button-sm p-button-outlined p-button-success"
        />
      </div>
    );
  };

  const showOverpanel = () => {
    setShowPackageModel(true);
  };

  return (
    <>
      <DataTable
        className="shadow-md"
        scrollable
        scrollHeight="400px"
        value={customerData}
        loading={loading}
        stripedRows
        lazy={true}
        paginator={true}
        filterDisplay="menu"
        responsiveLayout="scroll"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        dataKey="id"
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage="No Billing Codes found."
        totalRecords={totalRecords}
        rows={lazyParams.rows}
        first={lazyParams.first}
        onPage={onPage}
        onSort={onSort}
        sortOrder={lazyParams.sortOrder}
        header={renderFilters}
      >
        <Column field="name" header="Name"></Column>
        <Column field="description" header="Description"></Column>
        <Column
          field="createdDate"
          body={renderCreatedDate}
          header="Created Date"
        ></Column>
        <Column field="createdBy" header="Created By"></Column>
        <Column
          field="updatedDate"
          body={renderUpdatedDate}
          header="Updated Date"
        ></Column>
        <Column field="updatedBy" header="Updated By"></Column>
        <Column field="isActive" body={renderActive} header="Active"></Column>
        <Column body={renderActions} header="Actions"></Column>
      </DataTable>

      <Sidebar
        visible={showPackageModel}
        position="right"
        style={{ minHeight: 500 }}
        onHide={() => setShowPackageModel(false)}
      >
        <AddPackage
          onClose={() => setShowPackageModel(false)}
          customerId={customerData?.[0]?.customerId}
          onSubmitSuccess={loadLazyData}
        />
      </Sidebar>
    </>
  );
};

export default Packages;
